import { ApplicationConfig, LOCALE_ID, enableProdMode, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { storageGet } from '@scbt-lib/index';
import { IsAuthenticatedGuard } from '@scbt-shared/guards/isAuthenticated.guard';
import { APP_DOMAIN } from '@scbt-shared/tokens';
import { provideViewConsole } from '@scbt-shared/view-console-feature/view-console.provider';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';

import { HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MyHammerConfig } from '@scbt-shared/hammer.config';

// locale
import { registerLocaleData } from '@angular/common';
import localeItExtra from '@angular/common/locales/extra/it';
import localeIt from '@angular/common/locales/it';

registerLocaleData(localeIt, 'it', localeItExtra);

import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import moment from 'moment';
import 'moment/locale/it';

import { environment } from '@scbt-env/environment';

import { HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { SclobyInterceptor } from '@scbt-shared/interceptors/scloby.interceptor';
import { ScbTeamFirebaseFunctionsTokenInterceptor } from '@scbt-shared/interceptors/token.interceptor';

// Firebase — both projects (scb-team + scb-website) are wired up in one place, emulators included.
import { logFirebaseTargets, scbTeamFirebaseProviders, scbWebFirebaseProviders } from '@scbt-shared/firebase/firebase.providers';

import { provideHttpClient } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { provideRouter, withInMemoryScrolling, withPreloading, withViewTransitions } from '@angular/router';
import { NG0912SuppressorErrorHandler } from '@scbt-shared/error-handlers/ng0912-suppressor';
import { InSequenceGuard } from '@scbt-shared/guards/in-sequence.guard';
import { PermissionsGuard } from '@scbt-shared/guards/permissions.guard';
import '@scbt-shared/prototypes';
import { InjectorService } from '@scbt-shared/services/injector.service';
import 'hammerjs';
import { AppPreloadingStrategy } from './app/app-preloading-strategy';

const showConsole = storageGet('SHOW_DEV_CONSOLE') === 'true';
const SHOW_VIEW_CONSOLE_PRODIVER = showConsole ? [provideViewConsole()] : [];

export const appConfig: ApplicationConfig = {
	providers: [
		{ provide: ErrorHandler, useClass: NG0912SuppressorErrorHandler },
		provideAnimationsAsync(),
		provideRouter(
			appRoutes,
			withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
			// withDebugTracing()
			withPreloading(AppPreloadingStrategy),
			withViewTransitions()
			// withComponentInputBinding()
		),
		provideHttpClient(withInterceptorsFromDi()),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ScbTeamFirebaseFunctionsTokenInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: SclobyInterceptor,
			multi: true,
		},
		...SHOW_VIEW_CONSOLE_PRODIVER,
		{
			provide: APP_DOMAIN,
			useValue: `${window.location.host.startsWith('localhost') ? 'http://' : 'https://'}${window.location.host}`,
			multi: false,
		},
		// importProvidersFrom([MomentDateModule]),
		provideMomentDateAdapter(),
		{ provide: MAT_DATE_LOCALE, useValue: 'it' },
		{ provide: LOCALE_ID, useValue: 'it' },
		// scb-team must come first — it is the default FirebaseApp.
		...scbTeamFirebaseProviders,
		...scbWebFirebaseProviders,

		importProvidersFrom([
			NgxMaterialTimepickerModule.setOpts('it-IT', 'latn'),
			ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
			HammerModule,
		]),
		{
			provide: HAMMER_GESTURE_CONFIG,
			useClass: MyHammerConfig,
		},
		IsAuthenticatedGuard,
		PermissionsGuard,
		InSequenceGuard,
	],
};

if (environment.production) {
	enableProdMode();
}

bootstrapApplication(AppComponent, appConfig)
	.then((appRef) => {
		InjectorService.injector = appRef.injector;
		logFirebaseTargets();
		const matIconRegistry = appRef.injector.get(MatIconRegistry);
		matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');
		moment.locale('it');
	})
	.catch((err) => console.error(err));
